import { useEffect, useMemo, useState } from 'react';
import { useItemDetail } from '@/hooks/useInventory';
import type { BatchWithBalance, IssueEntry, Item, ItemDetail, ItemType } from '@/types/api';

/** One medicine queued to be issued: an item, the batch it comes from, a quantity. */
export interface IssueRow {
  rowId: string;
  itemId: string;
  type: ItemType;
  brandName: string;
  genericName: string | null;
  strength: string | null;
  batchId: string;
  batchNumber: string | null;
  expiryDate: string | null;
  /** Snapshot of remaining balance when the line was added. Server re-validates
   *  at submit; we surface oversell errors then. */
  batchBalance: number;
  quantity: number;
}

export interface DraftSelection {
  item: Item;
  /** Null while the item-detail fetch is in flight or while the user is
   *  picking a batch from the popover. */
  batch: BatchWithBalance | null;
  quantity: number;
}

export function rowToEntry(row: IssueRow): IssueEntry {
  return { itemId: row.itemId, batchId: row.batchId, quantity: row.quantity };
}

/**
 * The state behind "pick a medicine, pick its batch, set a quantity, add it to
 * the list" — shared by the standalone Issue screen and the visit form, which
 * dispense through the same picker and must not grow two versions of its rules
 * (FEFO default batch, out-of-stock refusal, quantity clamped to the batch).
 *
 * It owns no submission: the caller decides what the list is FOR and sends
 * `entries` wherever that belongs.
 */
export function useIssueLines() {
  const [pending, setPending] = useState<IssueRow[]>([]);
  const [draft, setDraft] = useState<DraftSelection | null>(null);
  const [scanOpen, setScanOpen] = useState(false);

  // Detail fetch for the currently-selected item (drives the batch picker).
  const itemId = draft?.item.id ?? null;
  const { data: detail, isLoading: detailLoading } = useItemDetail(itemId, {
    enabled: itemId !== null,
  });

  // Auto-attach the FEFO batch once the detail arrives. If there's exactly one
  // stocked batch, the user shouldn't have to make a redundant choice.
  useEffect(() => {
    if (!draft || !detail || draft.item.id !== detail.id) return;
    if (draft.batch) return; // user already chose; don't override their pick
    const stocked = detail.batches.filter((b) => b.balance > 0);
    if (stocked.length === 0) return; // shouldn't happen — picker blocks OOS items
    setDraft((d) => (d ? { ...d, batch: stocked[0]! } : d));
  }, [detail, draft]);

  const totals = useMemo(
    () => ({
      count: pending.length,
      units: pending.reduce((acc, r) => acc + r.quantity, 0),
    }),
    [pending],
  );

  const quantityValid = useMemo(() => {
    if (!draft || !draft.batch) return false;
    if (!Number.isInteger(draft.quantity) || draft.quantity <= 0) return false;
    return draft.quantity <= draft.batch.balance;
  }, [draft]);

  const pickItem = (item: Item) => {
    if (item.totalStock <= 0) return;
    setDraft({ item, batch: null, quantity: 1 });
  };

  const pickBatch = (batch: BatchWithBalance) => {
    setDraft((d) => (d ? { ...d, batch, quantity: Math.min(d.quantity, batch.balance) } : d));
  };

  const setDraftQuantity = (quantity: number) => {
    setDraft((d) => (d ? { ...d, quantity } : d));
  };

  const addRow = () => {
    if (!draft || !draft.batch || !quantityValid) return;
    const { item, batch, quantity } = draft;
    setPending((prev) => [
      ...prev,
      {
        rowId: crypto.randomUUID(),
        itemId: item.id,
        type: item.type,
        brandName: item.brandName,
        genericName: item.genericName,
        strength: item.strength,
        batchId: batch.id,
        batchNumber: batch.batchNumber,
        expiryDate: batch.expiryDate,
        batchBalance: batch.balance,
        quantity,
      },
    ]);
    setDraft(null);
  };

  const removeRow = (rowId: string) => {
    setPending((prev) => prev.filter((r) => r.rowId !== rowId));
  };

  const updateRowQuantity = (rowId: string, quantity: number) => {
    setPending((prev) =>
      prev.map((r) =>
        r.rowId === rowId
          ? { ...r, quantity: Math.max(1, Math.min(r.batchBalance, quantity)) }
          : r,
      ),
    );
  };

  const resolveScan = (item: ItemDetail) => {
    setScanOpen(false);
    if (item.totalStock <= 0) return; // dialog already showed OOS state
    const stocked = item.batches.filter((b) => b.balance > 0);
    setDraft({
      item: {
        ...item,
        // ItemDetail already includes these aggregates via the response.
        totalStock: item.totalStock,
        stockedBatchCount: stocked.length,
      },
      batch: stocked[0] ?? null,
      quantity: 1,
    });
  };

  /** Empty the list and any half-made selection — after a successful submit. */
  const reset = () => {
    setPending([]);
    setDraft(null);
  };

  return {
    pending,
    entries: pending.map(rowToEntry),
    totals,
    draft,
    detail,
    detailLoading,
    quantityValid,
    scanOpen,
    openScan: () => setScanOpen(true),
    closeScan: () => setScanOpen(false),
    resolveScan,
    pickItem,
    pickBatch,
    setDraftQuantity,
    cancelDraft: () => setDraft(null),
    addRow,
    removeRow,
    updateRowQuantity,
    reset,
  };
}

export type IssueLines = ReturnType<typeof useIssueLines>;
